import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetOfferByIdQuery, useUpdateOfferMutation } from '../Service/offersApi';
import { useEntityForm } from '../../Shared/Hooks/useEntityForm';
import { offerFields } from '../fields';
import { OffersRoutes } from '../../../Router/path';
import { BookingsRoutes } from '../../../Router/path';
import { FormRenderGroupTypes } from '../../Shared/Components/FormRenderer/FormRenderGroup.types';
import InfoIcon from '@mui/icons-material/Info';
import DescriptionIcon from '@mui/icons-material/Description';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import theme from '../../../theme';
import EntityFormLayout from '../../Shared/Components/EntityFormLayout/EntityFormLayout';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { Offer } from '../Types/offerTypes';
import { offerValues } from '../initialValues';
import { useGetOfferWithAssociationsByIdQuery } from '../Service/extendedOffersApi';
import { useGetCategoriesQuery } from '../../Categories/Service/categoriesApi';
import { useGetTagsQuery } from '../../Tags/Service/tagsApi';
import OfferBookingsTable from '../Components/OfferBooking/OfferBookingsTable';
import AccordionCard from '../../Shared/Components/AccordionCard/AccordionCard';
import BookOnlineIcon from '@mui/icons-material/BookOnline';
import { useGetCompaniesQuery } from '../../Companies/Service/companiesApi';
import { useGetBookingListQuery } from '../../Bookings/Service/extendedBookingsApi';

import EventNoteIcon from '@mui/icons-material/EventNote';
import OfferOptionMappingsSection from '../../OfferOptionsMappings/Components/OfferOptionMappingsSection/OfferOptionMappingsSection';
import { useGetOfferOptionTypesQuery } from '../../OfferOptions/Service/extendedOferOptionsApi';
import { useGetByOfferIdQuery } from '../../OfferOptionsMappings/Service/extendedOfferOptionsMappingsApi';
import OfferBoardingPlacesSection from '../../OfferBoardingPlaces/Components/OfferBoardingPlacesSection';
import OfferProgramTable from '../../OfferProgram/Components/OfferProgramTable';
import AccordionCardWithDialogButton from '../../Shared/Components/AccordionCardWithDialogButton/AccordionCardWithDialogButton';
import AutomaticBookingsDialog from '../Components/OfferBooking/AutomaticBookingsDialog';
import ImageIcon from '@mui/icons-material/Image';
import OfferImagesSection from '../Components/OfferImagesSection/OfferImagesSection';
import { Alert } from '@mui/material';
import { extractApiErrorMessage } from '../../../Utils/apiErrorUtils';
import { extractOfferEntityFormApiErrors } from '../Utils/offerEntityFormApiErrorHandling';
import { normalizeOfferForSubmit } from '../Utils/normalizeOfferForSubmit';
import AddOfferLookupModal from '../Components/AddOfferLookupModal/AddOfferLookupModal';
import {
  OFFER_LOOKUP_FIELDS,
  OfferLookupField,
  OfferLookupEntityOption,
  filterOfferLookupWithAddNew,
  mergeLookupOptions,
  NEW_OFFER_LOOKUP_OPTION_VALUE,
} from '../Utils/offerLookupAddNew';
import {
  useGetOfferLookupCountriesQuery,
  useGetOfferLookupHotelsQuery,
  useGetOfferLookupRegionsQuery,
} from '../Service/offersLocationLookupApi';

const EditOffer = () => {
  const [showBookingsTable, setShowBookingsTable] = useState(false);
  const [autocompleteResetKeys, setAutocompleteResetKeys] = useState<Record<string, number>>({});
  const [addLookupModalOpen, setAddLookupModalOpen] = useState(false);
  const [activeLookupField, setActiveLookupField] = useState<OfferLookupField>('countryId');
  const [offerLookupSearches, setOfferLookupSearches] = useState<Record<OfferLookupField, string>>({
    countryId: '',
    regionId: '',
    hotelId: '',
  });
  const [additionalLookupOptions, setAdditionalLookupOptions] = useState<
    Record<OfferLookupField, OfferLookupEntityOption[]>
  >({
    countryId: [],
    regionId: [],
    hotelId: [],
  });
  const offerId = parseInt(useParams<{ offerId: string }>().offerId!);
  const navigate = useNavigate();
  const {
    data: offerById,
    isLoading: isOfferLoading,
    error: offerError,
  } = useGetOfferByIdQuery(offerId, {
    skip: !offerId,
  });
  const { data: offerWithAllAssociationsData } = useGetOfferWithAssociationsByIdQuery(offerId);
  const [updateOffer, updateStatus] = useUpdateOfferMutation();

  const { data: hotels, isLoading: hotelsLoading } = useGetOfferLookupHotelsQuery();
  const { data: regions, isLoading: regionsLoading } = useGetOfferLookupRegionsQuery();
  const { data: countries, isLoading: countriesLoading } = useGetOfferLookupCountriesQuery();
  const { data: categories, isLoading: categoriesLoading } = useGetCategoriesQuery();
  const { data: tags, isLoading: tagsLoading } = useGetTagsQuery();
  const { data: offerOptionTypes, isLoading: offerOptionsLoading } = useGetOfferOptionTypesQuery();
  const { data: companies, isLoading: companiesLoading } = useGetCompaniesQuery();
  const {
    data: mappings,
    isLoading: mappingsLoading,
    refetch: refetchMappings,
  } = useGetByOfferIdQuery(offerId, { skip: !offerId });
  const { data: existingBookingsProbe, isLoading: isExistingBookingsProbeLoading } =
    useGetBookingListQuery(
      {
        offerId,
        pageNumber: 1,
        pageSize: 1,
        includeCancelled: true,
      },
      { skip: !offerId }
    );

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const debugParam = searchParams.get('debug');
    const debugEnabled =
      debugParam === '1' ||
      debugParam === 'true' ||
      (debugParam !== null && debugParam !== '0') ||
      searchParams.has('debug');

    if (!debugEnabled) {
      return;
    }

    // eslint-disable-next-line no-console
    console.log('[EditOffer debug] href:', window.location.href);
    // eslint-disable-next-line no-console
    console.log('[EditOffer debug] offerId:', offerId);
    // eslint-disable-next-line no-console
    console.log('[EditOffer debug] getById (/offers/:id):', {
      offerId: offerById?.id,
      companyId: offerById?.companyId,
      companyName: offerById?.companyName,
      hotelId: offerById?.hotelId,
      regionId: offerById?.regionId,
      countryId: offerById?.countryId,
    });
    // eslint-disable-next-line no-console
    console.log('[EditOffer debug] with-associations (/offers/with-associations/:id):', {
      companyId: (offerWithAllAssociationsData as any)?.companyId,
      companyName: (offerWithAllAssociationsData as any)?.companyName,
      offerCategories: (offerWithAllAssociationsData as any)?.offerCategories,
      offerTags: (offerWithAllAssociationsData as any)?.offerTags,
      offerOptions: (offerWithAllAssociationsData as any)?.offerOptions,
    });
    // eslint-disable-next-line no-console
    console.log('[EditOffer debug] companies lookup count:', companies?.length ?? 0);
  }, [offerId, offerById, offerWithAllAssociationsData, companies?.length]);
  const hotelLookupOptions = useMemo(
    () => mergeLookupOptions(hotels ?? [], additionalLookupOptions.hotelId),
    [hotels, additionalLookupOptions.hotelId]
  );

  const regionLookupOptions = useMemo(
    () => mergeLookupOptions(regions ?? [], additionalLookupOptions.regionId),
    [regions, additionalLookupOptions.regionId]
  );

  const countryLookupOptions = useMemo(
    () => mergeLookupOptions(countries ?? [], additionalLookupOptions.countryId),
    [countries, additionalLookupOptions.countryId]
  );

  const initialData: Offer = useMemo(() => {
    const initialOfferCategoryIds =
      offerWithAllAssociationsData?.offerCategories?.map((cat) => Number(cat.id)) ?? [];

    const initialOfferTagIds =
      offerWithAllAssociationsData?.offerTags?.map((tag) => Number(tag.id)) ?? [];
    const initialOfferOptions =
      offerWithAllAssociationsData?.offerOptions?.map((opt) => ({
        offerOptionsTypeId: opt.offerOptionsTypeId,
        offerOptionsValueId: opt.offerOptionsValueId,
      })) ?? [];

    const companyIdRaw = offerWithAllAssociationsData?.companyId ?? offerById?.companyId;
    const companyIdParsed =
      companyIdRaw === null || companyIdRaw === undefined || String(companyIdRaw).trim() === ''
        ? undefined
        : Number(companyIdRaw);
    const companyIdFallback = Number.isFinite(companyIdParsed as number)
      ? (companyIdParsed as number)
      : offerValues.companyId;

    return offerWithAllAssociationsData
      ? {
          ...offerWithAllAssociationsData,
          offerCategoryIds: initialOfferCategoryIds,
          offerTagIds: initialOfferTagIds,
          offerOptions: initialOfferOptions,
          companyId: companyIdFallback,
        }
      : offerValues;
  }, [offerWithAllAssociationsData, offerById]);

  const entityForm = useEntityForm<Offer>({
    initialData,
    fields: offerFields.map((field) => {
      if (field.type === 'autocomplete' && field.name === 'hotelId') {
        return {
          ...field,
          name: field.name as keyof Offer,
          options: hotelLookupOptions,
          loading: hotelsLoading,
        };
      }

      if (field.type === 'autocomplete' && field.name === 'regionId') {
        return {
          ...field,
          name: field.name as keyof Offer,
          options: regionLookupOptions,
          loading: regionsLoading,
        };
      }
      if (field.type === 'autocomplete' && field.name === 'countryId') {
        return {
          ...field,
          name: field.name as keyof Offer,
          options: countryLookupOptions,
          loading: countriesLoading,
        };
      }
      if (field.type === 'autocomplete' && field.name === 'offerCategoryIds' && categories) {
        return {
          ...field,
          name: field.name as keyof Offer,
          options: categories.map((category) => {
            const parent = categories.find((cat) => cat.id === category.parentCategoryId);
            return {
              label: parent ? `${parent.name} >>> ${category.name}` : category.name,
              value: category.id,
            };
          }),
          loading: categoriesLoading,
        };
      }
      if (field.type === 'autocomplete' && field.name === 'offerTagIds' && tags) {
        return {
          ...field,
          name: field.name as keyof Offer,
          options: tags?.map((tag) => {
            return {
              label: tag.name,
              value: tag.id,
            };
          }),
          loading: tagsLoading,
        };
      }
      // if (field.type === 'offerOptions' && offerOptionTypes) {
      //   return {
      //     ...field,
      //     name: field.name as keyof Offer,
      //     optionTypes: offerOptionTypes || [],
      //     loading: offerOptionsLoading,
      //   };
      // }
      if (field.type === 'autocomplete' && field.name === 'companyId' && companies) {
        return {
          ...field,
          name: field.name as keyof Offer,
          options: companies?.map((company) => {
            return {
              label: company.name,
              value: company.id,
            };
          }),
          loading: companiesLoading,
        };
      }
      return {
        ...field,
        name: field.name as keyof Offer,
      };
    }),
    submitFn: (data) => {
      const normalizedData = normalizeOfferForSubmit(data);
      return updateOffer({ id: offerId, data: normalizedData }).unwrap();
    },
    redirectUrl: OffersRoutes.root,
    title: 'Edytuj ofertę',
    icon: <LocalOfferIcon />,
    backUrl: OffersRoutes.root,
    groupOrder: [
      FormRenderGroupTypes.basic,
      FormRenderGroupTypes.additionalInformation,
      FormRenderGroupTypes.seo,
      FormRenderGroupTypes.configuration,
    ],
    groupIcons: {
      Podstawowe: <InfoIcon sx={{ color: theme.palette.primary.main }} />,
      'Dodatkowe informacje': <DescriptionIcon sx={{ color: theme.palette.primary.main }} />,
      SEO: <SearchIcon sx={{ color: theme.palette.primary.main }} />,
      Konfiguracja: <SettingsIcon sx={{ color: theme.palette.primary.main }} />,
      //Adres: <LocationOnIcon sx={{ color: theme.palette.primary.main }} />
    },
    mutationStatus: updateStatus,
    errorExtractor: extractOfferEntityFormApiErrors,
  });

  const createLookupSearchHandler = useCallback(
    (fieldName: OfferLookupField) =>
      (inputValue: string, reason?: 'input' | 'clear' | 'open') => {
        if (reason === 'open') {
          return;
        }

        setOfferLookupSearches((prev) => ({
          ...prev,
          [fieldName]: reason === 'clear' ? '' : inputValue,
        }));
      },
    []
  );

  const handleChangeWithLookupModal = useCallback(
    (event: any, child?: React.ReactNode) => {
      const { name, value } = event?.target ?? {};

      if (
        typeof name === 'string' &&
        OFFER_LOOKUP_FIELDS.includes(name as OfferLookupField) &&
        value === NEW_OFFER_LOOKUP_OPTION_VALUE
      ) {
        setAutocompleteResetKeys((prev) => ({
          ...prev,
          [name]: (prev[name] ?? 0) + 1,
        }));
        setActiveLookupField(name as OfferLookupField);
        setAddLookupModalOpen(true);
        return;
      }

      entityForm.formHandlers.onChange(event, child);
    },
    [entityForm.formHandlers]
  );

  const handleLookupCreated = useCallback(
    (payload: {
      field: OfferLookupField;
      id: number;
      label: string;
      relatedCountryId?: number;
    }) => {
      setAdditionalLookupOptions((prev) => ({
        ...prev,
        [payload.field]: mergeLookupOptions(prev[payload.field], [
          { label: payload.label, value: payload.id },
        ]),
      }));
      setOfferLookupSearches((prev) => ({
        ...prev,
        [payload.field]: '',
      }));

      if (payload.field === 'regionId' && payload.relatedCountryId) {
        entityForm.formHandlers.setFieldValue('countryId', payload.relatedCountryId);
      }

      entityForm.formHandlers.setFieldValue(payload.field, payload.id);
      setAddLookupModalOpen(false);
    },
    [entityForm.formHandlers]
  );

  if (isOfferLoading) {
    return (
      <Typography align="center" sx={{ mt: 4 }}>
        <CircularProgress />
      </Typography>
    );
  }

  if (offerError) {
    return (
      <Alert severity="error" sx={{ mt: 4 }}>
        {extractApiErrorMessage(offerError, 'Nie udało się załadować danych oferty.')}
      </Alert>
    );
  }

  const optionTypes = (offerOptionTypes || []).map((opt) => ({
    ...opt,
    offerOptionsValues: (opt.offerOptionsValues || []).map((val) => ({
      ...val,
      value: val.value ?? '', // Ensure value is always a string
    })),
  }));

  const hasExistingBookings = (existingBookingsProbe?.totalCount ?? 0) > 0;
  const shouldShowBookingsTable = showBookingsTable || hasExistingBookings;
  const selectedCountryId = Number(entityForm.formState.formData.countryId);
  const defaultCountryId =
    Number.isInteger(selectedCountryId) && selectedCountryId > 0 ? selectedCountryId : undefined;

  return (
    <>
      <AddOfferLookupModal
        open={addLookupModalOpen}
        field={activeLookupField}
        initialName={offerLookupSearches[activeLookupField]}
        defaultCountryId={defaultCountryId}
        countries={countryLookupOptions}
        onClose={() => setAddLookupModalOpen(false)}
        onCreated={handleLookupCreated}
      />
      <EntityFormLayout
        {...entityForm}
        formHandlers={{
          ...entityForm.formHandlers,
          onChange: handleChangeWithLookupModal,
        }}
        customFilterOptions={{
          countryId: (options, state) =>
            filterOfferLookupWithAddNew(options, state, 'nowy kraj'),
          regionId: (options, state) =>
            filterOfferLookupWithAddNew(options, state, 'nowy region'),
          hotelId: (options, state) =>
            filterOfferLookupWithAddNew(options, state, 'nowy hotel'),
        }}
        autocompleteInputChangeHandlers={{
          countryId: createLookupSearchHandler('countryId'),
          regionId: createLookupSearchHandler('regionId'),
          hotelId: createLookupSearchHandler('hotelId'),
        }}
        autocompleteResetKeys={autocompleteResetKeys}
      >
      <AccordionCard title="Opcje oferty">
        <OfferOptionMappingsSection
          offerId={offerId}
          offerOptionTypes={optionTypes}
          mappings={mappings || []}
          mappingsLoading={mappingsLoading}
          offerOptionsLoading={offerOptionsLoading}
          refetchMappings={refetchMappings}
        />
      </AccordionCard>
      <AccordionCard
        title={'Miejsca wsiadania'}
        icon={<LocalOfferIcon fontSize="large" color="primary" />}
      >
        <OfferBoardingPlacesSection offerId={offerId} /* templates={templates} */ />
      </AccordionCard>
      <AccordionCard
        title={'Program oferty'}
        icon={<EventNoteIcon fontSize="large" color="primary" />}
      >
        <OfferProgramTable offerId={offerId} />
      </AccordionCard>
      <AccordionCard
        title={
          shouldShowBookingsTable
            ? 'Lista automatycznych rezerwacji'
            : 'Stwórz automatycznie rezerwacje'
        }
        icon={<BookOnlineIcon fontSize="large" color="primary" />}
      >
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<BookOnlineIcon fontSize="small" />}
            onClick={() => navigate(`${BookingsRoutes.create}?offerId=${offerId}`)}
          >
            Dodaj rezerwację
          </Button>
        </Box>
        {isExistingBookingsProbeLoading ? (
          <Typography align="center" sx={{ mt: 2, mb: 2 }}>
            <CircularProgress size={24} />
          </Typography>
        ) : shouldShowBookingsTable ? (
          <OfferBookingsTable offerId={offerId} />
        ) : (
          <AccordionCardWithDialogButton
            buttonLabel="Stwórz automatyczne rezerwacje"
            DialogComponent={AutomaticBookingsDialog}
            dialogProps={{
              offerId,
              onSuccess: () => setShowBookingsTable(true),
            }}
          />
        )}
      </AccordionCard>
      <AccordionCard
        title="Zdjęcia oferty"
        icon={<ImageIcon fontSize="large" color="primary" />}
      >
        <OfferImagesSection
          offerId={offerId}
          currentMainImageId={offerWithAllAssociationsData?.mainImageId}
        />
      </AccordionCard>
      </EntityFormLayout>
    </>
  );
};

export default EditOffer;
